import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CategoryTile } from '../components/ui/CategoryTile';
import { Card } from '../components/ui/Card';
import { Spinner } from '../components/ui/Spinner';
import { ProductCard } from '../components/ui/ProductCard';
import { FeaturedStoryHero, StoryTile } from '../components/ui/StoryCard';
import { searchDevices } from '../services/devices';
import { listStories } from '../services/stories';
import { useAuth } from '../hooks/useAuth';
import type { Category } from '../types/models';

const CATEGORIES: { category: Category; label: string }[] = [
  { category: 'hifi', label: 'Hi-Fi' },
  { category: 'smartphone', label: 'Smartphones' },
  { category: 'laptop', label: 'Laptops' },
  { category: 'camera', label: 'Cameras' },
  { category: 'smart-home', label: 'Smart Home' },
  { category: 'gaming', label: 'Gaming' },
  { category: 'car', label: 'Cars' },
  { category: 'ebike', label: 'E-Bikes' },
  { category: 'watch', label: 'Watches' },
  { category: 'other', label: 'Other' },
];

export function HomePage() {
  const { isAuthenticated } = useAuth();
  const [params] = useSearchParams();
  const q = params.get('q') || '';
  const category = (params.get('category') as Category | null) || undefined;

  const stories = useQuery({ queryKey: ['stories'], queryFn: listStories });
  const catalog = useQuery({ queryKey: ['devices', 'catalog'], queryFn: () => searchDevices('') });

  const searchResults = useQuery({
    queryKey: ['devices', 'search', q, category],
    queryFn: () => searchDevices(q, category),
    enabled: q.length > 0 || !!category,
  });

  const isSearching = q.length > 0 || !!category;

  return (
    <div className="flex flex-col gap-10">
      {isSearching ? (
        <section>
          <h2 className="mb-4">Search results</h2>
          {searchResults.isLoading && <Spinner />}
          {searchResults.isError && (
            <Card className="text-accent-red">Could not load results. Please try again.</Card>
          )}
          {searchResults.isSuccess && searchResults.data.length === 0 && (
            <Card className="text-neutral-400">No devices match your search.</Card>
          )}
          {searchResults.isSuccess && searchResults.data.length > 0 && (
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {searchResults.data.map((d) => (
                <li key={d._id}>
                  <ProductCard device={d} />
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : (
        <section>
          <h2 className="mb-4">Featured stories</h2>
          {stories.isLoading && <Spinner />}
          {stories.isError && (
            <Card className="text-accent-red">Could not load stories right now.</Card>
          )}
          {stories.isSuccess && stories.data.length === 0 && (
            <Card className="text-neutral-400">No stories published yet.</Card>
          )}
          {stories.isSuccess && stories.data.length > 0 && stories.data[0] && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <FeaturedStoryHero story={stories.data[0]} className="lg:col-span-2" />
              {catalog.data?.[0] ? (
                <ProductCard device={catalog.data[0]} />
              ) : (
                <div className="card flex items-center justify-center text-neutral-500 text-sm">
                  {catalog.isLoading ? <Spinner /> : 'No devices yet'}
                </div>
              )}
              {stories.data.slice(1, 3).map((s) => (
                <StoryTile key={s._id} story={s} />
              ))}
            </div>
          )}
        </section>
      )}

      <section>
        <h2 className="mb-4">Browse by category</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          {CATEGORIES.map((c) => (
            <CategoryTile key={c.category} category={c.category} label={c.label} />
          ))}
        </div>
      </section>

      {!isAuthenticated && (
        <section className="card flex flex-col sm:flex-row items-center gap-8 py-8">
          <div className="flex-1">
            <h2 className="mb-2">Create an account</h2>
            <p className="text-neutral-400 mb-4 max-w-sm">
              to view the battery status of all your devices
            </p>
            <Link to="/register" className="btn-primary">
              Register now
            </Link>
          </div>
          <div className="w-full sm:w-72 shrink-0 rounded-xl2 border border-surface-500 bg-surface-700 p-3">
            <div className="rounded-lg bg-surface-900 p-3 flex flex-col gap-2">
              <div className="flex gap-1.5">
                <span className="w-2 h-2 rounded-full bg-accent-red" />
                <span className="w-2 h-2 rounded-full bg-accent-orange" />
                <span className="w-2 h-2 rounded-full bg-lime-500" />
              </div>
              {[80, 45, 90].map((w, i) => (
                <div key={i} className="h-2 rounded-full bg-surface-600 overflow-hidden">
                  <div
                    className={`h-full ${i === 1 ? 'bg-accent-orange' : 'bg-lime-500'}`}
                    style={{ width: `${w}%` }}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
